import { useState, useEffect, useRef } from "react";
import { usePermissions } from "@/context/PermissionContext";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Card, CardContent } from "@/components/ui/card";
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogFooter } from "@/components/ui/dialog";
import { AlertDialog, AlertDialogAction, AlertDialogCancel, AlertDialogContent, AlertDialogDescription, AlertDialogFooter, AlertDialogHeader, AlertDialogTitle } from "@/components/ui/alert-dialog";
import { Badge } from "@/components/ui/badge";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";
import { Tabs, TabsContent, TabsList, TabsTrigger } from "@/components/ui/tabs";
import { FileText, Plus, Pencil, Trash2, Upload, Loader2, Eye, FolderOpen, ChevronDown, ChevronRight, File, Plane, Package, BookOpen, Settings, Briefcase, FileCheck, Folder, ClipboardList, Shield, Wrench, AlertTriangle, Info, HelpCircle } from "lucide-react";
import { toast } from "sonner";
import api from "@/lib/api";
import { format, parseISO } from "date-fns";

// Icon mapping for dynamic sections
const ICON_MAP = {
  "Plane": Plane,
  "Package": Package,
  "BookOpen": BookOpen,
  "Briefcase": Briefcase,
  "FileCheck": FileCheck,
  "Folder": Folder,
  "ClipboardList": ClipboardList,
  "Shield": Shield,
  "Wrench": Wrench,
  "AlertTriangle": AlertTriangle,
  "Info": Info,
  "HelpCircle": HelpCircle,
  "FileText": FileText
};

// Color mapping
const COLOR_MAP = {
  "blue": { bg: "bg-blue-500", text: "text-blue-600", bgLight: "bg-blue-50" },
  "orange": { bg: "bg-orange-500", text: "text-orange-600", bgLight: "bg-orange-50" },
  "green": { bg: "bg-green-500", text: "text-green-600", bgLight: "bg-green-50" },
  "red": { bg: "bg-red-500", text: "text-red-600", bgLight: "bg-red-50" },
  "purple": { bg: "bg-purple-500", text: "text-purple-600", bgLight: "bg-purple-50" },
  "yellow": { bg: "bg-yellow-500", text: "text-yellow-600", bgLight: "bg-yellow-50" },
  "pink": { bg: "bg-pink-500", text: "text-pink-600", bgLight: "bg-pink-50" },
  "indigo": { bg: "bg-indigo-500", text: "text-indigo-600", bgLight: "bg-indigo-50" },
  "teal": { bg: "bg-teal-500", text: "text-teal-600", bgLight: "bg-teal-50" },
  "gray": { bg: "bg-gray-500", text: "text-gray-600", bgLight: "bg-gray-50" }
};

const AVAILABLE_ICONS = ["Plane", "Package", "BookOpen", "Briefcase", "FileCheck", "Folder", "ClipboardList", "Shield", "Wrench", "AlertTriangle", "Info", "HelpCircle", "FileText"];
const AVAILABLE_COLORS = ["blue", "orange", "green", "red", "purple", "yellow", "pink", "indigo", "teal", "gray"];

export default function ProceduresPage() {
  const { isMasterAdmin } = usePermissions();
  const [procedures, setProcedures] = useState([]);
  const [sections, setSections] = useState([]);
  const [loading, setLoading] = useState(true);
  const [expandedProcedure, setExpandedProcedure] = useState(null);
  const [activeTab, setActiveTab] = useState("");
  
  // Dialog states
  const [createDialogOpen, setCreateDialogOpen] = useState(false);
  const [editDialogOpen, setEditDialogOpen] = useState(false);
  const [deleteDialogOpen, setDeleteDialogOpen] = useState(false);
  const [uploadDialogOpen, setUploadDialogOpen] = useState(false);
  const [viewPdfDialogOpen, setViewPdfDialogOpen] = useState(false);
  const [deletePdfDialogOpen, setDeletePdfDialogOpen] = useState(false);
  const [manageSectionsDialogOpen, setManageSectionsDialogOpen] = useState(false);
  const [deleteSectionDialogOpen, setDeleteSectionDialogOpen] = useState(false);
  
  // Form states
  const [newTitle, setNewTitle] = useState("");
  const [newSection, setNewSection] = useState("");
  const [editTitle, setEditTitle] = useState("");
  const [editSection, setEditSection] = useState("");
  const [selectedProcedure, setSelectedProcedure] = useState(null);
  const [selectedPdf, setSelectedPdf] = useState(null);
  const [pdfBlobUrl, setPdfBlobUrl] = useState(null);
  const [submitting, setSubmitting] = useState(false);
  const [uploading, setUploading] = useState(false);
  const [loadingPdf, setLoadingPdf] = useState(false);
  
  // New section form
  const [newSectionName, setNewSectionName] = useState("");
  const [newSectionIcon, setNewSectionIcon] = useState("BookOpen");
  const [newSectionColor, setNewSectionColor] = useState("gray");
  const [sectionToDelete, setSectionToDelete] = useState(null);
  
  const fileInputRef = useRef(null);

  useEffect(() => {
    fetchData();
  }, []);

  const fetchData = async () => {
    setLoading(true);
    try {
      const [proceduresRes, sectionsRes] = await Promise.all([
        api.get("/procedures"),
        api.get("/procedures/sections")
      ]);
      setProcedures(proceduresRes.data);
      setSections(sectionsRes.data.sections || []);
      
      // Set first section as active if not set
      if (!activeTab && sectionsRes.data.sections?.length > 0) {
        setActiveTab(sectionsRes.data.sections[0].name);
      }
    } catch (error) {
      toast.error("Failed to fetch data");
    } finally {
      setLoading(false);
    }
  };

  const fetchProcedures = async () => {
    try {
      const response = await api.get("/procedures");
      setProcedures(response.data);
    } catch (error) {
      toast.error("Failed to fetch procedures");
    }
  };

  const fetchSections = async () => {
    try {
      const response = await api.get("/procedures/sections");
      setSections(response.data.sections || []);
    } catch (error) {
      toast.error("Failed to fetch sections");
    }
  };

  const handleCreateProcedure = async () => {
    if (!newTitle.trim()) {
      toast.error("Please enter a title");
      return;
    }
    
    setSubmitting(true);
    try {
      await api.post("/procedures", { title: newTitle.trim(), section: newSection || activeTab });
      toast.success("Procedure created successfully");
      setCreateDialogOpen(false);
      setNewTitle("");
      setNewSection("");
      fetchProcedures();
    } catch (error) {
      toast.error(error.response?.data?.detail || "Failed to create procedure");
    } finally {
      setSubmitting(false);
    }
  };

  const handleEditProcedure = async () => {
    if (!editTitle.trim()) {
      toast.error("Please enter a title");
      return;
    }
    
    setSubmitting(true);
    try {
      await api.put(`/procedures/${selectedProcedure.id}`, { title: editTitle.trim(), section: editSection });
      toast.success("Procedure updated successfully");
      setEditDialogOpen(false);
      setSelectedProcedure(null);
      setEditTitle("");
      setEditSection("");
      fetchProcedures();
    } catch (error) {
      toast.error(error.response?.data?.detail || "Failed to update procedure");
    } finally {
      setSubmitting(false);
    }
  };

  const handleDeleteProcedure = async () => {
    setSubmitting(true);
    try {
      await api.delete(`/procedures/${selectedProcedure.id}`);
      toast.success("Procedure deleted successfully");
      setDeleteDialogOpen(false);
      setSelectedProcedure(null);
      fetchProcedures();
    } catch (error) {
      toast.error(error.response?.data?.detail || "Failed to delete procedure");
    } finally {
      setSubmitting(false);
    }
  };

  const handleCreateSection = async () => {
    if (!newSectionName.trim()) {
      toast.error("Please enter a section name");
      return;
    }
    
    setSubmitting(true);
    try {
      await api.post("/procedures/sections", { 
        name: newSectionName.trim(), 
        icon: newSectionIcon,
        color: newSectionColor 
      });
      toast.success("Section created successfully");
      setNewSectionName("");
      setNewSectionIcon("BookOpen");
      setNewSectionColor("gray");
      fetchSections();
    } catch (error) {
      toast.error(error.response?.data?.detail || "Failed to create section");
    } finally {
      setSubmitting(false);
    }
  };

  const handleDeleteSection = async () => {
    setSubmitting(true);
    try {
      await api.delete(`/procedures/sections/${encodeURIComponent(sectionToDelete)}`);
      toast.success("Section deleted successfully");
      setDeleteSectionDialogOpen(false);
      setSectionToDelete(null);
      fetchSections();
      // Switch to first section if deleted section was active
      if (activeTab === sectionToDelete && sections.length > 1) {
        const remainingSections = sections.filter(s => s.name !== sectionToDelete);
        if (remainingSections.length > 0) {
          setActiveTab(remainingSections[0].name);
        }
      }
    } catch (error) {
      toast.error(error.response?.data?.detail || "Failed to delete section");
    } finally {
      setSubmitting(false);
    }
  };

  const handleFileSelect = async (e) => {
    const file = e.target.files?.[0];
    if (!file) return;
    
    if (!file.name.toLowerCase().endsWith('.pdf')) {
      toast.error("Please select a PDF file");
      return;
    }
    
    setUploading(true);
    try {
      const formData = new FormData();
      formData.append("file", file);
      
      await api.post(`/procedures/${selectedProcedure.id}/upload`, formData, {
        headers: { "Content-Type": "multipart/form-data" }
      });
      
      toast.success("PDF uploaded successfully");
      setUploadDialogOpen(false);
      fetchProcedures();
    } catch (error) {
      toast.error(error.response?.data?.detail || "Failed to upload PDF");
    } finally {
      setUploading(false);
      if (fileInputRef.current) {
        fileInputRef.current.value = "";
      }
    }
  };

  const handleViewPdf = async (procedure, pdf) => {
    setSelectedProcedure(procedure);
    setSelectedPdf(pdf);
    setLoadingPdf(true);
    setViewPdfDialogOpen(true);
    
    try {
      const response = await api.get(`/procedures/${procedure.id}/pdf/${pdf.id}`);
      const base64Data = response.data.data;
      
      // Convert base64 to blob for better handling of large files
      const byteCharacters = atob(base64Data);
      const byteNumbers = new Array(byteCharacters.length);
      for (let i = 0; i < byteCharacters.length; i++) {
        byteNumbers[i] = byteCharacters.charCodeAt(i);
      }
      const byteArray = new Uint8Array(byteNumbers);
      const blob = new Blob([byteArray], { type: 'application/pdf' });
      const blobUrl = URL.createObjectURL(blob);
      setPdfBlobUrl(blobUrl);
    } catch (error) {
      toast.error("Failed to load PDF");
      setViewPdfDialogOpen(false);
    } finally {
      setLoadingPdf(false);
    }
  };

  const handleDeletePdf = async () => {
    setSubmitting(true);
    try {
      await api.delete(`/procedures/${selectedProcedure.id}/pdf/${selectedPdf.id}`);
      toast.success("PDF deleted successfully");
      setDeletePdfDialogOpen(false);
      setSelectedPdf(null);
      fetchProcedures();
    } catch (error) {
      toast.error(error.response?.data?.detail || "Failed to delete PDF");
    } finally {
      setSubmitting(false);
    }
  };

  const openCreateDialog = () => {
    setNewSection(activeTab);
    setCreateDialogOpen(true);
  };

  const openEditDialog = (procedure) => {
    setSelectedProcedure(procedure);
    setEditTitle(procedure.title);
    setEditSection(procedure.section || "General Procedures");
    setEditDialogOpen(true);
  };

  const openDeleteDialog = (procedure) => {
    setSelectedProcedure(procedure);
    setDeleteDialogOpen(true);
  };

  const openUploadDialog = (procedure) => {
    setSelectedProcedure(procedure);
    setUploadDialogOpen(true);
  };

  const openDeletePdfDialog = (procedure, pdf) => {
    setSelectedProcedure(procedure);
    setSelectedPdf(pdf);
    setDeletePdfDialogOpen(true);
  };

  const toggleExpanded = (procedureId) => {
    setExpandedProcedure(expandedProcedure === procedureId ? null : procedureId);
  };

  const formatFileSize = (bytes) => {
    if (bytes < 1024) return `${bytes} B`;
    if (bytes < 1024 * 1024) return `${(bytes / 1024).toFixed(1)} KB`;
    return `${(bytes / (1024 * 1024)).toFixed(1)} MB`;
  };

  // Filter procedures by section
  const getProceduresBySection = (sectionName) => {
    return procedures.filter(p => (p.section || "General Procedures") === sectionName);
  };

  const getSectionConfig = (section) => {
    const IconComponent = ICON_MAP[section.icon] || BookOpen;
    const colorConfig = COLOR_MAP[section.color] || COLOR_MAP.gray;
    return { IconComponent, ...colorConfig };
  };

  const renderProcedureCard = (procedure) => {
    const isExpanded = expandedProcedure === procedure.id;
    const pdfCount = procedure.pdfs?.length || 0;
    const section = sections.find(s => s.name === procedure.section) || { icon: "BookOpen", color: "green" };
    const { IconComponent, bg } = getSectionConfig(section);
    
    return (
      <Card 
        key={procedure.id} 
        className="border-[#e2e8f0] dark:border-[#334155] hover:border-[#d4af37]/30 transition-colors"
        data-testid={`procedure-${procedure.id}`}
      >
        <CardContent className="p-0">
          {/* Procedure Header */}
          <div 
            className="flex items-center justify-between p-4 cursor-pointer hover:bg-[#f8fafc] dark:hover:bg-[#1e293b] rounded-t-lg"
            onClick={() => toggleExpanded(procedure.id)}
          >
            <div className="flex items-center gap-4">
              <div className={`w-12 h-12 ${bg} rounded-lg flex items-center justify-center`}>
                <IconComponent size={24} className="text-white" />
              </div>
              <div>
                <h3 className="text-lg font-semibold text-[#0f172a] dark:text-white font-['Outfit']">
                  {procedure.title}
                </h3>
                <div className="flex items-center gap-3 text-sm text-[#64748b] dark:text-gray-400">
                  <span>{pdfCount} PDF{pdfCount !== 1 ? "s" : ""}</span>
                  <span>•</span>
                  <span>Updated {format(parseISO(procedure.updated_at), "dd MMM yyyy")}</span>
                </div>
              </div>
            </div>
            
            <div className="flex items-center gap-2">
              {isMasterAdmin && (
                <>
                  <Button
                    variant="outline"
                    size="sm"
                    onClick={(e) => { e.stopPropagation(); openUploadDialog(procedure); }}
                    className="border-green-500 text-green-600 hover:bg-green-50"
                    data-testid={`upload-pdf-${procedure.id}`}
                  >
                    <Upload size={16} className="mr-1" />
                    Upload PDF
                  </Button>
                  <Button
                    variant="ghost"
                    size="icon"
                    onClick={(e) => { e.stopPropagation(); openEditDialog(procedure); }}
                    className="text-[#64748b] hover:text-[#0f172a] hover:bg-[#f1f5f9]"
                    data-testid={`edit-procedure-${procedure.id}`}
                  >
                    <Pencil size={16} />
                  </Button>
                  <Button
                    variant="ghost"
                    size="icon"
                    onClick={(e) => { e.stopPropagation(); openDeleteDialog(procedure); }}
                    className="text-[#64748b] hover:text-red-600 hover:bg-red-50"
                    data-testid={`delete-procedure-${procedure.id}`}
                  >
                    <Trash2 size={16} />
                  </Button>
                </>
              )}
              {isExpanded ? (
                <ChevronDown size={20} className="text-[#64748b]" />
              ) : (
                <ChevronRight size={20} className="text-[#64748b]" />
              )}
            </div>
          </div>
          
          {/* Expanded PDF List */}
          {isExpanded && (
            <div className="border-t border-[#e2e8f0] dark:border-[#334155] p-4 bg-[#f8fafc] dark:bg-[#0f172a]/50 rounded-b-lg">
              {pdfCount === 0 ? (
                <p className="text-center text-[#64748b] dark:text-gray-400 py-4">
                  No PDFs uploaded yet
                </p>
              ) : (
                <div className="space-y-2">
                  {procedure.pdfs.map((pdf) => (
                    <div 
                      key={pdf.id}
                      className="flex items-center justify-between p-3 bg-white dark:bg-[#1e293b] rounded-lg border border-[#e2e8f0] dark:border-[#334155]"
                    >
                      <div className="flex items-center gap-3">
                        <div className="w-10 h-10 bg-red-100 dark:bg-red-900/30 rounded-lg flex items-center justify-center">
                          <File size={20} className="text-red-600 dark:text-red-400" />
                        </div>
                        <div>
                          <p className="font-medium text-[#0f172a] dark:text-white">{pdf.filename}</p>
                          <p className="text-xs text-[#64748b] dark:text-gray-400">
                            {formatFileSize(pdf.size)} • Uploaded {format(parseISO(pdf.uploaded_at), "dd MMM yyyy HH:mm")}
                          </p>
                        </div>
                      </div>
                      <div className="flex items-center gap-2">
                        <Button
                          variant="outline"
                          size="sm"
                          onClick={() => handleViewPdf(procedure, pdf)}
                          className="border-blue-500 text-blue-600 hover:bg-blue-50"
                          data-testid={`view-pdf-${pdf.id}`}
                        >
                          <Eye size={16} className="mr-1" />
                          View
                        </Button>
                        {isMasterAdmin && (
                          <Button
                            variant="ghost"
                            size="icon"
                            onClick={() => openDeletePdfDialog(procedure, pdf)}
                            className="text-[#64748b] hover:text-red-600 hover:bg-red-50"
                            data-testid={`delete-pdf-${pdf.id}`}
                          >
                            <Trash2 size={16} />
                          </Button>
                        )}
                      </div>
                    </div>
                  ))}
                </div>
              )}
            </div>
          )}
        </CardContent>
      </Card>
    );
  };

  const renderSectionContent = (sectionName) => {
    const sectionProcedures = getProceduresBySection(sectionName);
    
    if (sectionProcedures.length === 0) {
      return (
        <div className="empty-state">
          <FolderOpen className="mx-auto" />
          <h3>No Procedures in {sectionName}</h3>
          <p>No procedures have been added to this section yet.</p>
          {isMasterAdmin && (
            <Button
              onClick={openCreateDialog}
              className="mt-4 bg-[#d4af37] hover:bg-[#b5952f] text-[#0f172a]"
            >
              <Plus size={20} className="mr-2" />
              Add Procedure
            </Button>
          )}
        </div>
      );
    }
    
    return (
      <div className="space-y-4">
        {sectionProcedures.map(renderProcedureCard)}
      </div>
    );
  };

  return (
    <div className="animate-fade-in" data-testid="procedures-page">
      {/* Header */}
      <div className="sticky-header-combined">
        <div className="flex items-center justify-between">
          <div>
            <h1 className="text-3xl font-bold text-[#0f172a] dark:text-white font-['Outfit']">Procedures</h1>
            <p className="text-[#64748b] dark:text-gray-400 mt-1">View and manage operational procedures</p>
          </div>
          <div className="flex items-center gap-3">
            {isMasterAdmin && (
              <>
                <Button
                  onClick={() => setManageSectionsDialogOpen(true)}
                  variant="outline"
                  className="border-[#64748b] text-[#64748b] hover:bg-[#f1f5f9]"
                  data-testid="manage-sections-btn"
                >
                  <Settings size={18} className="mr-2" />
                  Manage Sections
                </Button>
                <Button
                  onClick={openCreateDialog}
                  className="bg-[#d4af37] hover:bg-[#b5952f] text-[#0f172a] font-semibold"
                  data-testid="create-procedure-btn"
                >
                  <Plus size={20} className="mr-2" />
                  Add Procedure
                </Button>
              </>
            )}
            <Badge variant="secondary" className="bg-[#f1f5f9] text-[#64748b] text-lg px-4 py-2">
              {procedures.length} Procedure{procedures.length !== 1 ? "s" : ""}
            </Badge>
          </div>
        </div>
      </div>

      {/* Content */}
      <div className="page-content-with-sticky">
        {loading ? (
          <div className="flex items-center justify-center h-[40vh]">
            <Loader2 className="w-10 h-10 animate-spin text-[#d4af37]" />
          </div>
        ) : sections.length === 0 ? (
          <div className="empty-state">
            <FolderOpen className="mx-auto" />
            <h3>No Sections</h3>
            <p>No procedure sections have been created yet.</p>
            {isMasterAdmin && (
              <Button
                onClick={() => setManageSectionsDialogOpen(true)}
                className="mt-4 bg-[#d4af37] hover:bg-[#b5952f] text-[#0f172a]"
              >
                <Plus size={20} className="mr-2" />
                Create Section
              </Button>
            )}
          </div>
        ) : (
          <Tabs value={activeTab} onValueChange={setActiveTab} className="w-full">
            <TabsList className={`grid w-full mb-6 bg-[#f1f5f9] dark:bg-[#1e293b] p-1 rounded-lg`} style={{ gridTemplateColumns: `repeat(${sections.length}, minmax(0, 1fr))` }}>
              {sections.map((section) => {
                const { IconComponent, text, bgLight } = getSectionConfig(section);
                const count = getProceduresBySection(section.name).length;
                return (
                  <TabsTrigger 
                    key={section.name} 
                    value={section.name}
                    className="flex items-center gap-2 data-[state=active]:bg-white dark:data-[state=active]:bg-[#0f172a] data-[state=active]:shadow-sm rounded-md py-3 text-sm"
                    data-testid={`tab-${section.name.toLowerCase().replace(/\s/g, '-')}`}
                  >
                    <IconComponent size={18} className={text} />
                    <span className="font-medium truncate">{section.name}</span>
                    <Badge variant="secondary" className={`${bgLight} ${text} text-xs ml-1`}>
                      {count}
                    </Badge>
                  </TabsTrigger>
                );
              })}
            </TabsList>
            
            {sections.map(section => (
              <TabsContent key={section.name} value={section.name} className="mt-0">
                {renderSectionContent(section.name)}
              </TabsContent>
            ))}
          </Tabs>
        )}
      </div>

      {/* Manage Sections Dialog */}
      <Dialog open={manageSectionsDialogOpen} onOpenChange={setManageSectionsDialogOpen}>
        <DialogContent className="bg-white dark:bg-[#1e293b] max-w-lg">
          <DialogHeader>
            <DialogTitle className="text-[#0f172a] dark:text-white font-['Outfit']">
              Manage Sections
            </DialogTitle>
          </DialogHeader>
          <div className="space-y-6 py-4">
            {/* Create New Section */}
            <div className="space-y-4 p-4 bg-[#f8fafc] dark:bg-[#0f172a] rounded-lg">
              <h4 className="font-semibold text-[#0f172a] dark:text-white">Create New Section</h4>
              <div className="space-y-3">
                <div className="space-y-2">
                  <Label className="text-[#0f172a] dark:text-white">Section Name *</Label>
                  <Input
                    value={newSectionName}
                    onChange={(e) => setNewSectionName(e.target.value)}
                    placeholder="Enter section name..."
                    className="bg-white dark:bg-[#1e293b] border-[#e2e8f0] dark:border-[#334155]"
                    data-testid="new-section-name-input"
                  />
                </div>
                <div className="grid grid-cols-2 gap-3">
                  <div className="space-y-2">
                    <Label className="text-[#0f172a] dark:text-white">Icon</Label>
                    <Select value={newSectionIcon} onValueChange={setNewSectionIcon}>
                      <SelectTrigger className="bg-white dark:bg-[#1e293b] border-[#e2e8f0] dark:border-[#334155]">
                        <SelectValue />
                      </SelectTrigger>
                      <SelectContent className="bg-white dark:bg-[#1e293b]">
                        {AVAILABLE_ICONS.map(iconName => {
                          const IconComp = ICON_MAP[iconName];
                          return (
                            <SelectItem key={iconName} value={iconName}>
                              <div className="flex items-center gap-2">
                                <IconComp size={16} />
                                {iconName}
                              </div>
                            </SelectItem>
                          );
                        })}
                      </SelectContent>
                    </Select>
                  </div>
                  <div className="space-y-2">
                    <Label className="text-[#0f172a] dark:text-white">Color</Label>
                    <Select value={newSectionColor} onValueChange={setNewSectionColor}>
                      <SelectTrigger className="bg-white dark:bg-[#1e293b] border-[#e2e8f0] dark:border-[#334155]">
                        <SelectValue />
                      </SelectTrigger>
                      <SelectContent className="bg-white dark:bg-[#1e293b]">
                        {AVAILABLE_COLORS.map(color => (
                          <SelectItem key={color} value={color}>
                            <div className="flex items-center gap-2">
                              <div className={`w-4 h-4 rounded ${COLOR_MAP[color].bg}`} />
                              {color.charAt(0).toUpperCase() + color.slice(1)}
                            </div>
                          </SelectItem>
                        ))}
                      </SelectContent>
                    </Select>
                  </div>
                </div>
                <Button
                  onClick={handleCreateSection}
                  disabled={submitting || !newSectionName.trim()}
                  className="w-full bg-[#d4af37] hover:bg-[#b5952f] text-[#0f172a]"
                  data-testid="create-section-btn"
                >
                  {submitting ? <Loader2 className="h-4 w-4 animate-spin" /> : "Create Section"}
                </Button>
              </div>
            </div>
            
            {/* Existing Sections */}
            <div className="space-y-3">
              <h4 className="font-semibold text-[#0f172a] dark:text-white">Existing Sections</h4>
              {sections.length === 0 ? (
                <p className="text-[#64748b] dark:text-gray-400 text-center py-4">No sections created yet</p>
              ) : (
                <div className="space-y-2 max-h-60 overflow-y-auto">
                  {sections.map(section => {
                    const { IconComponent, bg, text } = getSectionConfig(section);
                    const count = getProceduresBySection(section.name).length;
                    return (
                      <div 
                        key={section.name}
                        className="flex items-center justify-between p-3 bg-[#f8fafc] dark:bg-[#0f172a] rounded-lg"
                      >
                        <div className="flex items-center gap-3">
                          <div className={`w-8 h-8 ${bg} rounded-md flex items-center justify-center`}>
                            <IconComponent size={16} className="text-white" />
                          </div>
                          <div>
                            <p className="font-medium text-[#0f172a] dark:text-white">{section.name}</p>
                            <p className="text-xs text-[#64748b] dark:text-gray-400">{count} procedure{count !== 1 ? "s" : ""}</p>
                          </div>
                        </div>
                        <Button
                          variant="ghost"
                          size="icon"
                          onClick={() => { setSectionToDelete(section.name); setDeleteSectionDialogOpen(true); }}
                          className="text-[#64748b] hover:text-red-600 hover:bg-red-50"
                          data-testid={`delete-section-${section.name}`}
                        >
                          <Trash2 size={16} />
                        </Button>
                      </div>
                    );
                  })}
                </div>
              )}
            </div>
          </div>
          <DialogFooter>
            <Button
              variant="outline"
              onClick={() => setManageSectionsDialogOpen(false)}
              className="border-[#e2e8f0] dark:border-[#334155]"
            >
              Close
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>

      {/* Delete Section Dialog */}
      <AlertDialog open={deleteSectionDialogOpen} onOpenChange={setDeleteSectionDialogOpen}>
        <AlertDialogContent className="bg-white dark:bg-[#1e293b]">
          <AlertDialogHeader>
            <AlertDialogTitle className="text-[#0f172a] dark:text-white">Delete Section</AlertDialogTitle>
            <AlertDialogDescription className="dark:text-gray-400">
              Are you sure you want to delete "{sectionToDelete}"? You can only delete sections that have no procedures.
            </AlertDialogDescription>
          </AlertDialogHeader>
          <AlertDialogFooter>
            <AlertDialogCancel className="border-[#e2e8f0] dark:border-[#334155]">Cancel</AlertDialogCancel>
            <AlertDialogAction
              onClick={handleDeleteSection}
              disabled={submitting}
              className="bg-red-600 hover:bg-red-700"
            >
              {submitting ? <Loader2 className="h-4 w-4 animate-spin" /> : "Delete"}
            </AlertDialogAction>
          </AlertDialogFooter>
        </AlertDialogContent>
      </AlertDialog>

      {/* Create Procedure Dialog */}
      <Dialog open={createDialogOpen} onOpenChange={setCreateDialogOpen}>
        <DialogContent className="bg-white dark:bg-[#1e293b] max-w-md">
          <DialogHeader>
            <DialogTitle className="text-[#0f172a] dark:text-white font-['Outfit']">
              Create New Procedure
            </DialogTitle>
          </DialogHeader>
          <div className="space-y-4 py-4">
            <div className="space-y-2">
              <Label className="text-[#0f172a] dark:text-white">Title *</Label>
              <Input
                value={newTitle}
                onChange={(e) => setNewTitle(e.target.value)}
                placeholder="Enter procedure title..."
                className="bg-white dark:bg-[#0f172a] border-[#e2e8f0] dark:border-[#334155]"
                data-testid="procedure-title-input"
              />
            </div>
            <div className="space-y-2">
              <Label className="text-[#0f172a] dark:text-white">Section *</Label>
              <Select value={newSection || activeTab} onValueChange={setNewSection}>
                <SelectTrigger className="bg-white dark:bg-[#0f172a] border-[#e2e8f0] dark:border-[#334155]" data-testid="procedure-section-select">
                  <SelectValue placeholder="Select section" />
                </SelectTrigger>
                <SelectContent className="bg-white dark:bg-[#1e293b]">
                  {sections.map((section) => {
                    const { IconComponent, text } = getSectionConfig(section);
                    return (
                      <SelectItem key={section.name} value={section.name}>
                        <div className="flex items-center gap-2">
                          <IconComponent size={16} className={text} />
                          {section.name}
                        </div>
                      </SelectItem>
                    );
                  })}
                </SelectContent>
              </Select>
            </div>
          </div>
          <DialogFooter>
            <Button
              variant="outline"
              onClick={() => setCreateDialogOpen(false)}
              className="border-[#e2e8f0] dark:border-[#334155]"
            >
              Cancel
            </Button>
            <Button
              onClick={handleCreateProcedure}
              disabled={submitting}
              className="bg-[#d4af37] hover:bg-[#b5952f] text-[#0f172a]"
              data-testid="save-procedure-btn"
            >
              {submitting ? <Loader2 className="h-4 w-4 animate-spin" /> : "Create"}
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>

      {/* Edit Procedure Dialog */}
      <Dialog open={editDialogOpen} onOpenChange={setEditDialogOpen}>
        <DialogContent className="bg-white dark:bg-[#1e293b] max-w-md">
          <DialogHeader>
            <DialogTitle className="text-[#0f172a] dark:text-white font-['Outfit']">
              Edit Procedure
            </DialogTitle>
          </DialogHeader>
          <div className="space-y-4 py-4">
            <div className="space-y-2">
              <Label className="text-[#0f172a] dark:text-white">Title *</Label>
              <Input
                value={editTitle}
                onChange={(e) => setEditTitle(e.target.value)}
                placeholder="Enter procedure title..."
                className="bg-white dark:bg-[#0f172a] border-[#e2e8f0] dark:border-[#334155]"
                data-testid="edit-procedure-title-input"
              />
            </div>
            <div className="space-y-2">
              <Label className="text-[#0f172a] dark:text-white">Section *</Label>
              <Select value={editSection} onValueChange={setEditSection}>
                <SelectTrigger className="bg-white dark:bg-[#0f172a] border-[#e2e8f0] dark:border-[#334155]" data-testid="edit-procedure-section-select">
                  <SelectValue placeholder="Select section" />
                </SelectTrigger>
                <SelectContent className="bg-white dark:bg-[#1e293b]">
                  {sections.map((section) => {
                    const { IconComponent, text } = getSectionConfig(section);
                    return (
                      <SelectItem key={section.name} value={section.name}>
                        <div className="flex items-center gap-2">
                          <IconComponent size={16} className={text} />
                          {section.name}
                        </div>
                      </SelectItem>
                    );
                  })}
                </SelectContent>
              </Select>
            </div>
          </div>
          <DialogFooter>
            <Button
              variant="outline"
              onClick={() => setEditDialogOpen(false)}
              className="border-[#e2e8f0] dark:border-[#334155]"
            >
              Cancel
            </Button>
            <Button
              onClick={handleEditProcedure}
              disabled={submitting}
              className="bg-[#d4af37] hover:bg-[#b5952f] text-[#0f172a]"
              data-testid="update-procedure-btn"
            >
              {submitting ? <Loader2 className="h-4 w-4 animate-spin" /> : "Update"}
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>

      {/* Delete Procedure Dialog */}
      <AlertDialog open={deleteDialogOpen} onOpenChange={setDeleteDialogOpen}>
        <AlertDialogContent className="bg-white dark:bg-[#1e293b]">
          <AlertDialogHeader>
            <AlertDialogTitle className="text-[#0f172a] dark:text-white">Delete Procedure</AlertDialogTitle>
            <AlertDialogDescription className="dark:text-gray-400">
              Are you sure you want to delete "{selectedProcedure?.title}"? This will also delete all associated PDFs. This action cannot be undone.
            </AlertDialogDescription>
          </AlertDialogHeader>
          <AlertDialogFooter>
            <AlertDialogCancel className="border-[#e2e8f0] dark:border-[#334155]">Cancel</AlertDialogCancel>
            <AlertDialogAction
              onClick={handleDeleteProcedure}
              disabled={submitting}
              className="bg-red-600 hover:bg-red-700"
            >
              {submitting ? <Loader2 className="h-4 w-4 animate-spin" /> : "Delete"}
            </AlertDialogAction>
          </AlertDialogFooter>
        </AlertDialogContent>
      </AlertDialog>

      {/* Upload PDF Dialog */}
      <Dialog open={uploadDialogOpen} onOpenChange={setUploadDialogOpen}>
        <DialogContent className="bg-white dark:bg-[#1e293b] max-w-md">
          <DialogHeader>
            <DialogTitle className="text-[#0f172a] dark:text-white font-['Outfit']">
              Upload PDF to "{selectedProcedure?.title}"
            </DialogTitle>
          </DialogHeader>
          <div className="space-y-4 py-4">
            <input
              ref={fileInputRef}
              type="file"
              accept=".pdf"
              onChange={handleFileSelect}
              className="hidden"
              data-testid="pdf-file-input"
            />
            <div 
              className="border-2 border-dashed border-[#e2e8f0] dark:border-[#334155] rounded-lg p-8 text-center cursor-pointer hover:border-[#d4af37] transition-colors"
              onClick={() => fileInputRef.current?.click()}
            >
              {uploading ? (
                <div className="flex flex-col items-center">
                  <Loader2 className="h-10 w-10 animate-spin text-[#d4af37] mb-2" />
                  <p className="text-[#64748b] dark:text-gray-400">Uploading...</p>
                </div>
              ) : (
                <>
                  <Upload className="h-10 w-10 text-[#64748b] dark:text-gray-400 mx-auto mb-2" />
                  <p className="text-[#0f172a] dark:text-white font-medium">Click to select PDF file</p>
                  <p className="text-sm text-[#64748b] dark:text-gray-400 mt-1">Only PDF files are allowed</p>
                </>
              )}
            </div>
          </div>
          <DialogFooter>
            <Button
              variant="outline"
              onClick={() => setUploadDialogOpen(false)}
              className="border-[#e2e8f0] dark:border-[#334155]"
            >
              Cancel
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>

      {/* View PDF Dialog */}
      <Dialog open={viewPdfDialogOpen} onOpenChange={setViewPdfDialogOpen}>
        <DialogContent className="bg-white dark:bg-[#1e293b] max-w-6xl max-h-[90vh]">
          <DialogHeader>
            <DialogTitle className="text-[#0f172a] dark:text-white font-['Outfit'] flex items-center gap-2">
              <File className="text-red-600" size={20} />
              {selectedPdf?.filename}
            </DialogTitle>
          </DialogHeader>
          <div className="py-4">
            {loadingPdf ? (
              <div className="flex items-center justify-center h-[60vh]">
                <Loader2 className="h-10 w-10 animate-spin text-[#d4af37]" />
              </div>
            ) : pdfBlobUrl ? (
              <div className="relative h-[70vh]">
                <embed
                  src={pdfBlobUrl}
                  type="application/pdf"
                  className="w-full h-full rounded-lg border border-[#e2e8f0] dark:border-[#334155]"
                />
                {/* Fallback overlay that shows if PDF doesn't render */}
                <div className="absolute inset-0 flex flex-col items-center justify-center bg-[#f8fafc] dark:bg-[#0f172a]/90 rounded-lg pointer-events-none opacity-0 peer-placeholder-shown:opacity-100" id="pdf-fallback">
                </div>
                {/* Always show Open in New Tab button */}
                <div className="absolute bottom-4 right-4 flex gap-2">
                  <Button
                    onClick={() => {
                      window.open(pdfBlobUrl, '_blank');
                    }}
                    size="sm"
                    className="bg-[#d4af37] hover:bg-[#b5952f] text-[#0f172a] shadow-lg"
                  >
                    <Eye size={16} className="mr-2" />
                    Open in New Tab
                  </Button>
                </div>
              </div>
            ) : (
              <div className="flex flex-col items-center justify-center h-[60vh] bg-[#f8fafc] dark:bg-[#0f172a] rounded-lg">
                <File size={48} className="text-red-500 mb-4" />
                <p className="text-[#0f172a] dark:text-white font-medium">Failed to load PDF</p>
                <p className="text-sm text-[#64748b] dark:text-gray-400 mt-2">Please try again or contact support</p>
              </div>
            )}
          </div>
          <DialogFooter>
            <Button
              variant="outline"
              onClick={() => { 
                setViewPdfDialogOpen(false); 
                if (pdfBlobUrl) {
                  URL.revokeObjectURL(pdfBlobUrl);
                  setPdfBlobUrl(null);
                }
              }}
              className="border-[#e2e8f0] dark:border-[#334155]"
            >
              Close
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>

      {/* Delete PDF Dialog */}
      <AlertDialog open={deletePdfDialogOpen} onOpenChange={setDeletePdfDialogOpen}>
        <AlertDialogContent className="bg-white dark:bg-[#1e293b]">
          <AlertDialogHeader>
            <AlertDialogTitle className="text-[#0f172a] dark:text-white">Delete PDF</AlertDialogTitle>
            <AlertDialogDescription className="dark:text-gray-400">
              Are you sure you want to delete "{selectedPdf?.filename}"? This action cannot be undone.
            </AlertDialogDescription>
          </AlertDialogHeader>
          <AlertDialogFooter>
            <AlertDialogCancel className="border-[#e2e8f0] dark:border-[#334155]">Cancel</AlertDialogCancel>
            <AlertDialogAction
              onClick={handleDeletePdf}
              disabled={submitting}
              className="bg-red-600 hover:bg-red-700"
            >
              {submitting ? <Loader2 className="h-4 w-4 animate-spin" /> : "Delete"}
            </AlertDialogAction>
          </AlertDialogFooter>
        </AlertDialogContent>
      </AlertDialog>
    </div>
  );
}
